import {
    CircularProgress,
    createStyles,
    List,
    ListItem,
    ListItemText,
    makeStyles,
    Paper,
    TextField,
    Typography,
} from "@material-ui/core";
import React from "react";
import LocationService, { LocationOption } from "../../services/location/location.service";

const useStyles = makeStyles((theme) =>
    createStyles({
        root: {
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(1),
        },
        inputWrap: {
            position: "relative",
        },
        input: {
            minWidth: 300,
            [theme.breakpoints.down("sm")]: {
                minWidth: "unset",
            },
        },
        map: {
            width: "100%",
            height: 320,
            borderRadius: 8,
            overflow: "hidden",
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
        },
        helper: {
            color: "rgba(255, 255, 255, 0.75)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: theme.spacing(1),
            flexWrap: "wrap",
        },
        warning: {
            color: "#ffcc80",
        },
        loading: {
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(1),
            color: "rgba(255, 255, 255, 0.8)",
        },
        suggestionList: {
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            marginTop: theme.spacing(1),
            zIndex: 4,
            maxHeight: 260,
            overflowY: "auto",
        },
    })
);

const LEAFLET_SCRIPT_ID = "leaflet-script";
const LEAFLET_STYLE_ID = "leaflet-style";
const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 };
const DEFAULT_ZOOM = 5;
const FOCUSED_ZOOM = 17;
const BASE_MAP_TILE_URL = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}";
const BASE_MAP_ATTRIBUTION = "Tiles &copy; Esri";

let loadLeafletPromise: Promise<void> | null = null;
let leafletMarkerIcon: any = null;

const loadLeafletAssets = () => {
    if ((window as any).L) {
        return Promise.resolve();
    }

    if (loadLeafletPromise) {
        return loadLeafletPromise;
    }

    loadLeafletPromise = new Promise((resolve, reject) => {
        const existingStyle = document.getElementById(LEAFLET_STYLE_ID);
        if (!existingStyle) {
            const link = document.createElement("link");
            link.id = LEAFLET_STYLE_ID;
            link.rel = "stylesheet";
            link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
            document.head.appendChild(link);
        }

        const existingScript = document.getElementById(LEAFLET_SCRIPT_ID) as HTMLScriptElement | null;
        if (existingScript) {
            existingScript.addEventListener("load", () => resolve(), { once: true });
            existingScript.addEventListener("error", () => reject(new Error("Leaflet failed to load.")), { once: true });
            return;
        }

        const script = document.createElement("script");
        script.id = LEAFLET_SCRIPT_ID;
        script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("Leaflet failed to load."));
        document.head.appendChild(script);
    });

    return loadLeafletPromise;
};

const getLeafletMarkerIcon = (L: any) => {
    if (leafletMarkerIcon) {
        return leafletMarkerIcon;
    }

    leafletMarkerIcon = L.icon({
        iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
        iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
        shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41],
    });

    return leafletMarkerIcon;
};

interface Props {
    value?: string;
    lat?: number;
    lng?: number;
    fullWidth?: boolean;
    placeholder?: string;
    onChange?: (value: string) => void;
    onSelect?: (location: LocationOption) => void;
}

const getFinitePosition = (lat?: number, lng?: number) => {
    const parsedLat = Number(lat);
    const parsedLng = Number(lng);
    if (!Number.isFinite(parsedLat) || !Number.isFinite(parsedLng)) {
        return undefined;
    }

    return { lat: parsedLat, lng: parsedLng };
};

const toLatLngText = (nextLat: number, nextLng: number) => `${nextLat.toFixed(6)}, ${nextLng.toFixed(6)}`;

export default function GoogleMapLocationPicker({
    value,
    lat,
    lng,
    fullWidth,
    placeholder,
    onChange,
    onSelect,
}: Props) {
    const classes = useStyles();
    const mapContainerRef = React.useRef<HTMLDivElement | null>(null);
    const mapRef = React.useRef<any>(null);
    const markerRef = React.useRef<any>(null);
    const mapClickHandlerRef = React.useRef<any>(null);
    const markerDragHandlerRef = React.useRef<any>(null);
    const selectedCoordsRef = React.useRef<{ lat: number; lng: number } | null>(null);

    const [address, setAddress] = React.useState(value || "");
    const [loading, setLoading] = React.useState(false);
    const [ready, setReady] = React.useState(false);
    const [searching, setSearching] = React.useState(false);
    const [resolving, setResolving] = React.useState(false);
    const [results, setResults] = React.useState<LocationOption[]>([]);
    const [focused, setFocused] = React.useState(false);
    const [geoDenied, setGeoDenied] = React.useState(false);

    const addressRef = React.useRef(address);
    React.useEffect(() => {
        addressRef.current = address;
    }, [address]);

    React.useEffect(() => {
        setAddress(value || "");
    }, [value]);

    const emitSelection = React.useCallback((nextAddress: string, nextLat: number, nextLng: number) => {
        selectedCoordsRef.current = { lat: nextLat, lng: nextLng };
        onSelect?.({
            address: nextAddress,
            lat: nextLat,
            lng: nextLng,
        });
    }, [onSelect]);

    const reverseResolve = React.useCallback(async (nextLat: number, nextLng: number) => {
        setResolving(true);
        try {
            const resolved = await LocationService.reverse(nextLat, nextLng);
            setAddress(resolved.address);
            onChange?.(resolved.address);
            emitSelection(resolved.address, nextLat, nextLng);
        } catch (error) {
            const fallbackAddress = toLatLngText(nextLat, nextLng);
            setAddress(fallbackAddress);
            onChange?.(fallbackAddress);
            emitSelection(fallbackAddress, nextLat, nextLng);
        } finally {
            setResolving(false);
        }
    }, [emitSelection, onChange]);

    const setMarkerPosition = React.useCallback((position: { lat: number; lng: number }, options?: { panTo?: boolean; zoom?: number }) => {
        const L = (window as any).L;
        if (!L || !mapRef.current) {
            return;
        }

        if (!markerRef.current) {
            markerRef.current = L.marker([position.lat, position.lng], {
                draggable: true,
                icon: getLeafletMarkerIcon(L),
            }).addTo(mapRef.current);

            markerDragHandlerRef.current = markerRef.current.on("dragend", (event: any) => {
                const marker = event?.target;
                const nextLatLng = marker?.getLatLng?.();
                if (!nextLatLng) {
                    return;
                }

                reverseResolve(nextLatLng.lat, nextLatLng.lng);
            });
        } else {
            markerRef.current.setLatLng([position.lat, position.lng]);
        }

        selectedCoordsRef.current = { lat: position.lat, lng: position.lng };

        if (options?.panTo !== false) {
            mapRef.current.panTo([position.lat, position.lng]);
        }

        if (Number.isFinite(options?.zoom)) {
            mapRef.current.setZoom(options?.zoom);
        }
    }, [reverseResolve]);

    React.useEffect(() => {
        setLoading(true);
        loadLeafletAssets()
            .then(() => setReady(true))
            .catch(() => setReady(false))
            .finally(() => setLoading(false));
    }, []);

    React.useEffect(() => {
        if (!ready || !mapContainerRef.current || mapRef.current) {
            return;
        }

        const L = (window as any).L;
        if (!L) {
            return;
        }

        const initialPosition = getFinitePosition(lat, lng);
        const start = initialPosition || DEFAULT_CENTER;

        mapRef.current = L.map(mapContainerRef.current, {
            center: [start.lat, start.lng],
            zoom: initialPosition ? FOCUSED_ZOOM : DEFAULT_ZOOM,
            zoomControl: true,
        });

        L.tileLayer(BASE_MAP_TILE_URL, {
            attribution: BASE_MAP_ATTRIBUTION,
        }).addTo(mapRef.current);

        setMarkerPosition(start, {
            zoom: initialPosition ? FOCUSED_ZOOM : DEFAULT_ZOOM,
            panTo: true,
        });

        mapClickHandlerRef.current = mapRef.current.on("click", (event: any) => {
            const nextLat = event?.latlng?.lat;
            const nextLng = event?.latlng?.lng;
            if (!Number.isFinite(nextLat) || !Number.isFinite(nextLng)) {
                return;
            }

            setMarkerPosition({ lat: nextLat, lng: nextLng }, { zoom: FOCUSED_ZOOM, panTo: true });
            reverseResolve(nextLat, nextLng);
        });

        if (initialPosition) {
            return;
        }

        if (!navigator.geolocation) {
            setGeoDenied(true);
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                setGeoDenied(false);
                const nextLat = position.coords.latitude;
                const nextLng = position.coords.longitude;
                setMarkerPosition({ lat: nextLat, lng: nextLng }, { zoom: 16, panTo: true });
                if (!addressRef.current.trim()) {
                    reverseResolve(nextLat, nextLng);
                }
            },
            () => {
                setGeoDenied(true);
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0,
            }
        );
    }, [lat, lng, ready, reverseResolve, setMarkerPosition]);

    React.useEffect(() => {
        const nextPosition = getFinitePosition(lat, lng);
        if (!ready || !nextPosition) {
            return;
        }

        setMarkerPosition(nextPosition, { panTo: true, zoom: FOCUSED_ZOOM });
    }, [lat, lng, ready, setMarkerPosition]);

    React.useEffect(() => {
        const trimmed = (address || "").trim();
        if (trimmed.length < 3) {
            setSearching(false);
            setResults([]);
            return;
        }

        const controller = new AbortController();
        const timer = window.setTimeout(async () => {
            setSearching(true);
            try {
                const items = await LocationService.search(trimmed, { signal: controller.signal });
                setResults(items);
            } catch (error) {
                if (controller.signal.aborted) {
                    return;
                }
                setResults([]);
            } finally {
                if (!controller.signal.aborted) {
                    setSearching(false);
                }
            }
        }, 300);

        return () => {
            controller.abort();
            window.clearTimeout(timer);
        };
    }, [address]);

    React.useEffect(() => {
        return () => {
            if (mapRef.current) {
                mapRef.current.off("click", mapClickHandlerRef.current);
            }
            if (markerRef.current) {
                markerRef.current.off("dragend", markerDragHandlerRef.current);
            }
            if (mapRef.current) {
                mapRef.current.remove();
            }
            mapClickHandlerRef.current = null;
            markerDragHandlerRef.current = null;
            markerRef.current = null;
            mapRef.current = null;
        };
    }, []);

    const handleAddressChange = (nextAddress: string) => {
        setAddress(nextAddress);
        onChange?.(nextAddress);
    };

    const handleSelect = (location: LocationOption) => {
        setAddress(location.address);
        onChange?.(location.address);
        emitSelection(location.address, location.lat, location.lng);
        setResults([]);
        setFocused(false);
        setMarkerPosition({ lat: location.lat, lng: location.lng }, { panTo: true, zoom: FOCUSED_ZOOM });
    };

    const showSuggestions = focused && results.length > 0;
    return (
        <div className={classes.root}>
            {loading ? (
                <div className={classes.loading}>
                    <CircularProgress size={14} color="inherit" />
                    <Typography variant="caption">Loading street map...</Typography>
                </div>
            ) : null}

            <div ref={mapContainerRef} className={classes.map} />

            <div className={classes.inputWrap}>
                <TextField
                    className={classes.input}
                    classes={{ root: "lightInput" }}
                    InputProps={{ style: { backgroundColor: "#fff" } }}
                    fullWidth={fullWidth}
                    variant="outlined"
                    placeholder={placeholder}
                    value={address}
                    onChange={(event) => handleAddressChange(event.currentTarget.value)}
                    onFocus={() => setFocused(true)}
                    onBlur={() => window.setTimeout(() => setFocused(false), 120)}
                />

                {showSuggestions ? (
                    <Paper className={classes.suggestionList}>
                        <List dense>
                            {results.map((item, index) => (
                                <ListItem
                                    key={`${item.lat}_${item.lng}_${index}`}
                                    button
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => handleSelect(item)}
                                >
                                    <ListItemText primary={item.address} />
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                ) : null}
            </div>

            <Typography variant="caption" className={classes.helper}>
                <span className={geoDenied ? classes.warning : undefined}>
                    {resolving
                        ? "Resolving address from marker..."
                        : (geoDenied
                            ? "Location denied. Search, click map, or drag marker to pick."
                            : "Search, click map, or drag marker to pick exact location.")}
                </span>
            </Typography>

            {searching ? (
                <div className={classes.loading}>
                    <CircularProgress size={14} color="inherit" />
                    <Typography variant="caption">Searching addresses...</Typography>
                </div>
            ) : null}
        </div>
    );
}
